import { createInstance } from '@/helpers/api/base'
import { useQueryClient } from '@tanstack/react-query'
import { useAppMutation } from '@/helpers/api/mutation'
import { useNotification } from '@/ui/Notification'
import styles from '../ui/components/information/Unfinished.module.scss'
import { Log } from '@/types/logs'
import LockIcon from '../assets/lock.svg'
import UnlockIcon from '../assets/unlock.svg'
import { useCombinedLogsTableStateStore } from '../store'

interface ILockControllerLogRequest {
    id: string
    isLocked: boolean
}

export const useLockControllerLog = () => {
    const queryClient = useQueryClient()
    const { showNotification } = useNotification()
    const { setMultipleSelection } = useCombinedLogsTableStateStore()

    const updateVehicleAllocationMutation = useAppMutation<Log[], Error, ILockControllerLogRequest[]>({
        mutationFn: (data) =>
            createInstance({
                url: '/controller-logs/lock',
                method: 'PATCH',
                data,
            }),
        queryKey: ['controllerLogs'],
        alertOnError: true,
        onSuccess: async (data) => {
            if (data.length > 0) {
                // Update the lock status based on the response
                queryClient.setQueriesData<Log[]>({ queryKey: ['controllerLogs'] }, (previousLogs) => {
                    if (!previousLogs) return previousLogs
                    const lockStateById = new Map(
                        data.map(
                            (item) =>
                                [item.id, { locked: item.isLocked, codingPeriodInfo: item.codingPeriodInfo }] as const
                        )
                    )

                    return previousLogs.map((log) =>
                        lockStateById.has(log.id)
                            ? {
                                  ...log,
                                  isLocked: lockStateById.get(log.id)!.locked,
                                  codingPeriodInfo: lockStateById.get(log.id)!.codingPeriodInfo,
                              }
                            : log
                    )
                })

                // Update the UI
                const isAllLocked = data.every((row) => row.isLocked)
                showNotification({
                    className: styles.flaggedNotification,
                    message: (
                        <>
                            {!isAllLocked ? (
                                <>
                                    <UnlockIcon />
                                    <p>{data.length > 1 ? 'Records' : 'Record'} unlocked</p>
                                </>
                            ) : (
                                <>
                                    <LockIcon />
                                    <p>{data.length > 1 ? 'Records' : 'Record'} locked</p>
                                </>
                            )}
                        </>
                    ),
                })
            }

            setMultipleSelection([])
        },
    })

    const handleLockControllerLog = (data: Pick<ILockControllerLogRequest, 'id' | 'isLocked'>[]) => {
        if (data.length === 0) return
        updateVehicleAllocationMutation.mutate(data)
    }

    return { handleLockControllerLog, isPending: updateVehicleAllocationMutation.isPending }
}
